import React, { FC, useMemo } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Label,
  Cell,
} from "recharts";
import { generateColors } from "./colors";
import styles from "./charts.module.css";
import { InfoCircleOutlined } from "@ant-design/icons";
import { Tooltip as AntdTooltip } from "antd";
import { Empty } from "antd";

const {
  ["container"]: chartContainerClassName,
  ["chart-title"]: chartTitleClassName,
  ["chart-wrapper"]: chartWrapperClassName,
  ["custom-tooltip"]: customTooltipClassName,
} = styles;

interface PracticeAreaData {
  practiceArea: {
    id: number;
    name: string;
  };
  byStatus: { field: string; count: number }[];
  byLinkedUnlinked: { field: string; count: number }[];
  templatesByTemplateType: { field: string; count: number }[];
}

interface CountBarChartProps {
  data: PracticeAreaData[];
  title: string;
  explanatoryText?: string;
  width?: number;
  height?: number;
  countByField: "byStatus" | "byLinkedUnlinked" | "templatesByTemplateType";
}

const CountBarChart: FC<CountBarChartProps> = ({
  data,
  title,
  explanatoryText,
  width = 600,
  height = 400,
  countByField,
}) => {
  const processedData = useMemo(() => {
    return data
      .map((item) => {
        const processedItem: { name: string; [key: string]: string | number } = {
          name: item.practiceArea?.name,
          ...Object.fromEntries(item[countByField].map((status) => [status.field, status.count])),
        };
        // Only include items that have at least one non-zero count
        return Object.values(processedItem).some((value) => typeof value === "number" && value > 0)
          ? processedItem
          : null;
      })
      .filter((item): item is NonNullable<typeof item> => item !== null);
  }, [data, countByField]);

  const statusFields = useMemo(() => {
    const fields = new Set<string>();
    processedData.forEach((item) => {
      Object.entries(item).forEach(([key, value]) => {
        if (key !== "name" && typeof value === "number" && value > 0) {
          fields.add(key);
        }
      });
    });
    return Array.from(fields);
  }, [processedData]);

  const colors = useMemo(() => generateColors(statusFields.length), [statusFields]);

  const truncateText = (text: string, maxLength: number) => {
    return text.length > maxLength ? text.substring(0, maxLength) + "..." : text;
  };

  const getTotalForItem = (item: Record<string, any>) => {
    return statusFields.reduce((total, field) => total + (item[field] || 0), 0);
  };

  if (!data || data.length === 0) {
    return (
      <div className={chartContainerClassName} style={{ width, height }}>
        <h2 className={chartTitleClassName}>
          {title}{" "}
          {explanatoryText && (
            <AntdTooltip placement="top" title={explanatoryText}>
              <InfoCircleOutlined style={{ paddingLeft: "5px" }} />
            </AntdTooltip>
          )}
        </h2>
        <div className={chartWrapperClassName}>
          <div className={styles["empty-container"]}>
            <Empty description="No data" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={chartContainerClassName} style={{ width, height }}>
      <h2 className={chartTitleClassName}>
        {title}{" "}
        {explanatoryText && (
          <AntdTooltip placement="top" title={explanatoryText}>
            <InfoCircleOutlined style={{ paddingLeft: "5px" }} />
          </AntdTooltip>
        )}
      </h2>
      <div className={chartWrapperClassName}>
        <ResponsiveContainer width={width} height={height}>
          <BarChart data={processedData} margin={{ top: 20, right: 30, left: 20, bottom: 100 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="name"
              interval={0}
              tick={({ x, y, payload }) => (
                <g transform={`translate(${x},${y})`}>
                  <text x={0} y={0} dy={16} textAnchor="end" fill="#666" transform="rotate(-35)">
                    {truncateText(payload.value, 20)}
                  </text>
                </g>
              )}
            />
            <YAxis>
              <Label value="Count" angle={-90} position="insideLeft" />
            </YAxis>
            <Tooltip
              content={({ active, payload, label }) => {
                if (active && payload && payload.length) {
                  const itemTotal = getTotalForItem(payload[0].payload);
                  return (
                    <div className={customTooltipClassName}>
                      <p className="label">{`${label}`}</p>
                      {payload.map((entry, index) => {
                        const count = entry.value as number;
                        const percentage = ((count / itemTotal) * 100).toFixed(0);
                        return (
                          <p key={`item-${index}`} style={{ color: entry.color }}>
                            {`${entry.name}: ${count} (${percentage}%)`}
                          </p>
                        );
                      })}
                    </div>
                  );
                }
                return null;
              }}
            />
            <Legend
              verticalAlign="top"
              height={36}
              wrapperStyle={{ textAlign: "center", width: "100%" }}
              formatter={(value, entry, index) => <span style={{ color: "black" }}>{value}</span>}
            />
            {statusFields.map((field, index) => (
              <Bar key={field} dataKey={field} stackId="a" fill={colors[index % colors.length]}>
                {processedData.map((entry, entryIndex) => (
                  <Cell
                    key={`cell-${field}-${entryIndex}`}
                    fill={colors[index % colors.length]}
                    fillOpacity={(entry[field] as number) > 0 ? 1 : 0}
                  />
                ))}
              </Bar>
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default CountBarChart;
